import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/button/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog'
import { Input } from '@/components/input/input'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { addTaskActionMutation } from '@/lib/queries/business/v1/task/action/mutations'
import { cn } from '@/lib/utils'

export interface CreateTaskActionDialogProps {
  open: boolean
  taskId: number
  onClose: VoidFunction
}

export const CreateTaskActionDialog = ({ open, taskId, onClose }: CreateTaskActionDialogProps) => {
  const [taskInput, setTaskInput] = useState('')

  const { mutateAsync: addTaskAction, isPending: isAddingTaskAction } = useMutation(addTaskActionMutation)

  const isDisabledCreateButton = !taskInput
  const isLoadingCreateButton = isAddingTaskAction

  const handleChangeOpen = (open: boolean) => {
    if (!open) {
      onClose()
      return
    }
  }

  const handleClickCreateButton = async () => {
    await addTaskAction(
      { taskId, title: taskInput },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v1.task.action.index(taskId)],
          })
          toast.success('Task has been created!')
          onClose()
        },
        onError: () => {
          toast.error('Failed to create task. Please try again later.')
        },
      }
    )
  }

  const handleClickCancelButton = () => {
    onClose()
  }

  useEffect(
    function resetInputValue() {
      if (open) {
        setTaskInput('')
      }
    },
    [open]
  )

  return (
    <Dialog open={open} onOpenChange={handleChangeOpen}>
      <DialogContent className={cn('w-[360px]')}>
        <DialogHeader>
          <DialogTitle>Create Other Task</DialogTitle>
          <DialogDescription className={cn('sr-only')}>
            You can create other tasks for this specific case.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label className={cn('flex flex-col gap-y-2')}>
            <span className={cn('text-sm font-medium text-text')}>Task name</span>
            <Input
              placeholder="Please enter a task."
              value={taskInput}
              onChange={($event) => setTaskInput($event.target.value)}
            />
          </label>
        </div>

        <DialogFooter className={cn('mt-2')}>
          <Button
            type="button"
            variant="default"
            disabled={isDisabledCreateButton}
            loading={isLoadingCreateButton}
            className={cn('flex-1')}
            onClick={handleClickCreateButton}
          >
            Create
          </Button>
          <Button type="button" variant="secondary" className={cn('flex-1')} onClick={handleClickCancelButton}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
